import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "../../../components";
import { cn } from "../../../utils/bem";
import "./Config.prefix.css";
import { EMPTY_CONFIG } from "./Template";
import { API_CONFIG } from "../../../config/ApiConfig";
import { useAPI } from "../../../providers/ApiProvider";

const configClass = cn("configure");

// Lazy load Label Studio with a single promise to avoid multiple loads
// and enable as early as possible to load the dependencies once this component is mounted for the first time
let dependencies;
const loadDependencies = async () => {
  if (!dependencies) {
    dependencies = import("@humansignal/editor");
  }
  return dependencies;
};

export const Preview = ({ config, data, error, loading, project }) => {
  // @see comment about dependencies above
  loadDependencies();

  const [storeReady, setStoreReady] = useState(false);
  const lsf = useRef(null);
  const rootRef = useRef();
  const mountedRef = useRef(true);
  const api = useAPI();
  const projectRef = useRef(project);
  projectRef.current = project;

  const currentTask = useMemo(() => {
    return {
      id: 1,
      annotations: [],
      predictions: [],
      data,
    };
  }, [data]);

  /**
   * Proxy urls to presign them if storage is connected
   * @param {*} _ LS instance
   * @param {string} url http/https are not proxied and returned as is
   */
  const onPresignUrlForProject = async (_, url) => {
    // if URL is a relative, presigned url (url matches /tasks|projects/:id/resolve/.*) make it absolute
    const presignedUrlPattern = /^\/(?:tasks|projects)\/\d+\/resolve\/?/;
    if (presignedUrlPattern.test(url)) {
      url = new URL(url, document.location.origin).toString();
    }

    const parsedUrl = new URL(url);

    // return same url if http(s)
    if (["http:", "https:"].includes(parsedUrl.protocol)) return url;

    const projectId = projectRef.current.id;

    const fileuri = btoa(url);

    return api.api.createUrl(API_CONFIG.endpoints.presignUrlForProject, { projectId, fileuri }).url;
  };

  const currentConfig = useMemo(() => {
    // empty string causes error in LSF
    return config ?? EMPTY_CONFIG;
  }, [config]);

  const initLabelStudio = useCallback(async (config, task) => {
    await loadDependencies();

    if (lsf.current || !task.data) return;

    // Resolve root by id so we don't pass null after remounts; getElementById finds the current DOM node
    const rootEl = document.getElementById("label-studio") ?? rootRef.current;
    if (!rootEl) return;

    try {
      lsf.current = new window.LabelStudio(rootEl, {
        config,
        task,
        interfaces: ["side-column"],
        // Use the editor's React 18 code path so we stop getting the
        // legacy ReactDOM.render deprecation warning in the console.
        instanceOptions: { reactVersion: "v18" },
        // with SharedStore we should use more late event
        onStorageInitialized(LS) {
          try {
            LS.settings?.setProperty?.("bottomSidePanel", true);
          } catch (_e) {
            /* protected MST node — ignore if the action is unavailable */
          }

          const initAnnotation = () => {
            const as = LS.annotationStore;
            const c = as.createAnnotation();

            as.selectAnnotation(c.id);
            setStoreReady(true);
          };

          // and even then we need to wait a little even after the store is initialized
          setTimeout(initAnnotation);
        },
      });

      lsf.current.on("presignUrlForProject", onPresignUrlForProject);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    const opacity = loading || error ? 0.6 : 1;
    const el = document.getElementById("label-studio");
    if (el) el.style.opacity = opacity;
  }, [loading, error]);

  useEffect(() => {
    mountedRef.current = true;

    // Full teardown + rebuild on every config / task change. The older
    // ``resetState() → assignConfig() → initializeStore()`` hot-swap kept
    // MST tree references alive in React subscribers (TextArea.perregion,
    // RichTextPieceView._handleUpdate, ClassificationBase.result …), which
    // then fired against already-detached nodes and logged "not part of a
    // state tree". Destroying and recreating is a bit heavier but stays
    // consistent with the unmount path and avoids the stale subscriptions.
    if (lsf.current) {
      try {
        lsf.current.destroy();
      } catch {
        /* ignore */
      }
      lsf.current = null;
    }
    const rootEl = document.getElementById("label-studio") ?? rootRef.current;
    if (rootEl) rootEl.innerHTML = "";

    initLabelStudio(currentConfig, currentTask);

    return () => {
      mountedRef.current = false;
    };
  }, [currentConfig, currentTask]);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
      if (lsf.current) {
        try {
          lsf.current.destroy();
        } catch {
          /* ignore */
        }
        lsf.current = null;
      }
    };
  }, []);

  return (
    <div className={configClass.elem("preview").toClassName()}>
      <h3>Preview</h3>
      {error && (
        <div className={configClass.elem("preview-error").toClassName()}>
          <h2>
            {error.detail} {error.id}
          </h2>
          {error.validation_errors?.non_field_errors?.map?.((err) => (
            <p key={err}>{err}</p>
          ))}
          {error.validation_errors?.label_config?.map?.((err) => (
            <p key={err}>{err}</p>
          ))}
          {error.validation_errors?.map?.((err) => (
            <p key={err}>{err}</p>
          ))}
        </div>
      )}
      {!data && loading && (
        // Center a normal-sized spinner in the preview area. Passing the
        // 100%/50vh dims to <Spinner> directly stretched the brand mark
        // itself to fill the box (the "loading branding too big" bug).
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "50vh" }}>
          <Spinner size={48} />
        </div>
      )}
      <div id="label-studio" className={configClass.elem("preview-ui").toClassName()} ref={rootRef} />
    </div>
  );
};
